import { useTemplateFieldRuleSets } from "@/hooks/useTemplateFieldRuleSets";
import { useTemplateFields } from "@/hooks/useTemplateFields";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useEffect, useRef, useState } from "react";
import type { Datapoint } from "@/types/dataset";
import { toast } from "sonner";
import { EditRuleSetDialog } from "./EditRuleSetDialog";
import { NewRuleSetDialog } from "./NewRuleSetDialog";
import { RuleSet } from "./RuleSet";
import type { TemplateFieldWithDatapoints } from "./Rule";
type RuleSetRow = Database["public"]["Tables"]["template_field_rule_sets"]["Row"] & {
  rules: Database["public"]["Tables"]["template_field_rules"]["Row"][];
};

interface RuleSetsManagerProps {
  templateFieldId: string;
  templateId: string;
}

interface RpcDatapointRow {
  created_at: string;
  datapoint_id: number;
  field_key: string;
  template_field_id: number;
  template_id: number;
  value: string;
}

interface RpcDatapointSimpleRow {
  created_at: string;
  updated_at: string;
  id: string;
  name: string;
  dataset_id: string;
}

type RpcRow = RpcDatapointRow | RpcDatapointSimpleRow;

export const RuleSetsManager = ({ templateFieldId }: RuleSetsManagerProps) => {
  const [error, setError] = useState<Error | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedRuleSet, _setSelectedRuleSet] = useState<RuleSetRow | null>(null);
  const [expandedRuleSets, setExpandedRuleSets] = useState<Set<string>>(new Set());

  const { templateFields } = useTemplateFields();

  const {
    ruleSets,
    isPending,
    error: ruleSetError,
    createRuleSet,
    updateRuleSet,
    deleteRuleSet,
    createRule,
    updateRule,
    deleteRule,
  } = useTemplateFieldRuleSets({
    templateFieldId,
  });

  useEffect(() => {
    if (ruleSetError) {
      setError(ruleSetError);
    }
  }, [ruleSetError]);

  const [isFetchingDatapoints, setIsFetchingDatapoints] = useState(false);
  const [templateFieldsWithDatapoints, setTemplateFieldsWithDatapoints] = useState<Array<TemplateFieldWithDatapoints>>(
    [],
  );

  interface RpcDatapointRow {
    created_at: string;
    datapoint_id: number;
    field_key: string;
    template_field_id: number;
    template_id: number;
    value: string;
  }

  interface RpcDatapointSimpleRow {
    created_at: string;
    updated_at: string;
    id: string;
    name: string;
    dataset_id: string;
  }

  type RpcRow = RpcDatapointRow | RpcDatapointSimpleRow;

  // Used for rendering optimisation
  const templateFieldLengthRef = useRef(-1);

  useEffect(() => {
    if (templateFieldLengthRef.current === templateFields?.length) {
      return;
    }

    templateFieldLengthRef.current = templateFields?.length;
    // On mount, fetch the datapoints for each template field
    const fetchDatapointsForFields = async () => {
      if (!templateFields?.length) return;

      setIsFetchingDatapoints(true);
      try {
        const rpcPromises = templateFields.map((field) =>
          supabase.rpc("get_datapoints_for_template_field", {
            p_template_field_id: field.id,
          }),
        );

        const rpcResults = await Promise.all(rpcPromises);

        const templateFieldsWithDatapoints = templateFields.map((templateField, index) => {
          const { data, error } = rpcResults[index];
          if (error) throw error;
          const rawRows = (data ?? []) as RpcRow[];
          const mappedDatapoints: Datapoint[] = Array.isArray(rawRows)
            ? rawRows.map((row) =>
                "datapoint_id" in row
                  ? {
                      id: String(row.datapoint_id),
                      name: String(row.value ?? ""),
                      dataset_id: templateField.field.dataset?.id ?? "",
                    }
                  : {
                      id: row.id,
                      name: row.name,
                      dataset_id: row.dataset_id,
                    },
              )
            : [];
          return {
            ...templateField,
            field: {
              ...templateField.field,
              dataset: {
                id: templateField.field.dataset?.id ?? "",
                name: templateField.field.dataset?.name ?? "",
                datapoints: mappedDatapoints,
              },
            },
          };
        });

        setTemplateFieldsWithDatapoints(templateFieldsWithDatapoints);
      } catch (err) {
        console.error("Error fetching datapoints:", err);
        toast.error("Failed to load datapoints for fields");
        setError(err as Error);
      } finally {
        setIsFetchingDatapoints(false);
      }
    };

    fetchDatapointsForFields();
  }, [templateFields]);

  const templateFieldsToSelectFrom =
    templateFieldsWithDatapoints?.filter((templateField) => templateField.id !== templateFieldId) ?? [];

  useEffect(() => {
    if (error) {
      toast.error("Failed to load Rule Set Manager data");
    }
  }, [error]);

  const handleUpdateRuleSetMetadata = async (name: string, description?: string) => {
    if (!selectedRuleSet) return;

    try {
      await updateRuleSet({
        id: selectedRuleSet.id,
        name,
        description,
      });
      toast.success("Rule set updated successfully");
      setIsEditDialogOpen(false);
    } catch (_error) {
      toast.error("Failed to update rule set");
    }
  };

  const handleCreateRuleSet = async (name: string, description?: string) => {
    try {
      await createRuleSet({ name, description });
      toast.success("Rule set created successfully");
      setIsAddDialogOpen(false);
    } catch (_error) {
      toast.error("Failed to create rule set");
    }
  };

  const handleDeleteRuleSet = async (ruleSetId: string) => {
    try {
      await deleteRuleSet(ruleSetId);
      toast.success("Rule set deleted successfully");
    } catch (_error) {
      toast.error("Failed to delete rule set");
    }
  };

  const handleCreateRule = async (
    ruleSetId: string,
    rule: {
      fieldId: string;
      datapointId: string;
      operator: "AND" | "NOT";
    },
  ) => {
    const ruleSet = ruleSets.find((rs) => rs.id === ruleSetId);
    if (ruleSet) {
      try {
        await createRule(ruleSetId, rule);
        toast.success("Rule added successfully");
      } catch (_error) {
        toast.error("Failed to add rule");
      }
    } else {
      toast.error("Rule set not found - cannot create rule");
    }
  };

  const handleUpdateRule = async (
    ruleId: string,
    updateArgs: {
      fieldId: string;
      datapointId: string;
      operator: "AND" | "NOT";
    },
  ) => {
    try {
      await updateRule(ruleId, {
        fieldId: updateArgs.fieldId,
        datapointId: updateArgs.datapointId,
        operator: updateArgs.operator,
      });
      toast.success("Rule updated successfully");
    } catch (_error) {
      toast.error("Failed to update rule");
    }
  };

  const handleDeleteRule = async (_ruleSetId: string, ruleId: string) => {
    try {
      await deleteRule(ruleId);
      toast.success("Rule deleted successfully");
    } catch (_error) {
      toast.error("Failed to delete rule");
    }
  };

  const handleToggleRuleSet = (ruleSetId: string) => {
    setExpandedRuleSets((prev) => {
      const next = new Set(prev);
      if (next.has(ruleSetId)) {
        next.delete(ruleSetId);
      } else {
        next.add(ruleSetId);
      }
      return next;
    });
  };

  if (error) {
    return <div>Error loading data: {error.message}</div>;
  }

  if (isPending || isFetchingDatapoints) {
    return <div>Loading rule sets...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">Rule Sets</h4>
        <NewRuleSetDialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen} onSave={handleCreateRuleSet} />
      </div>

      {ruleSets.length > 0 ? (
        ruleSets.map((ruleSet) => (
          <RuleSet
            key={ruleSet.id}
            ruleSet={ruleSet}
            availableTemplateFields={templateFieldsToSelectFrom}
            onUpdateRuleSetMetadata={handleUpdateRuleSetMetadata}
            onDeleteRuleSet={handleDeleteRuleSet}
            onCreateRule={handleCreateRule}
            onUpdateRule={handleUpdateRule}
            onDeleteRule={handleDeleteRule}
            isExpanded={expandedRuleSets.has(ruleSet.id)}
            onToggleExpanded={() => handleToggleRuleSet(ruleSet.id)}
          />
        ))
      ) : (
        <p className="text-sm text-muted-foreground">No rule sets defined</p>
      )}

      <EditRuleSetDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        ruleSet={selectedRuleSet}
        onSave={handleUpdateRuleSetMetadata}
      />
    </div>
  );
};
